import React, { useState, useEffect } from 'react'
import Avatar from '@components/app/avatar'
import Curve from '@components/app/curve'
import Parallax from '@components/app/parallax'
import Button from '@components/common/button'
import Linkify from '@components/common/linkify'
import Spinner from '@components/common/spinner'
import { updateMe } from '@data/api/auth'
import { addFeature } from '@data/api/feature'
import classnames from 'classnames'
import dynamic from 'next/dynamic'
import { FormattedMessage, useIntl } from 'react-intl'
import useAuth from '@/hooks/auth'
import { UserProps } from '../../../types/user'
import Latest from './latest'
import SocialButton from './social-button'
import editStyles from './edit.module.scss'
import styles from './user.module.scss'

const ImageUploader = dynamic(() => import('@components/app/image-uploader'), {
  ssr: false,
})

interface Props {
  isMe: boolean
  isAdmin: boolean
  // isFollowing: boolean
  user: UserProps
  onUpdate: (user: UserProps) => void
}
const filenames = {
  user_image: '',
  cover_image: '',
}

const User = ({ isMe, isAdmin, onUpdate, ...props }: Props) => {
  const intl = useIntl()
  // states
  // eslint-disable-next-line react/destructuring-assignment
  const [ user, setUser ] = useState(props.user)
  const [ isEditing, setEditing ] = useState(false)
  const [ isSaving, setSaving ] = useState(false)
  const { updateUser } = useAuth()

  useEffect(
    () => {
      setUser(props.user)
      setEditing(false)
      setSaving(false)
    },
    // eslint-disable-next-line react/destructuring-assignment
    [ props.user ],
  )

  // methods
  const onToggleEditClick = () => {
    filenames.user_image = ''
    filenames.cover_image = ''
    if (isEditing) {
      setUser({ ...props.user })
    }
    setEditing(!isEditing)
  }

  // eslint-disable-next-line camelcase
  const onCoverChange = (cover_image: string) => {
    // eslint-disable-next-line camelcase
    filenames.cover_image = cover_image
    setUser({ ...user, cover_image })
  }
  // eslint-disable-next-line camelcase
  const onAvatarChange = (user_image) => {
    // eslint-disable-next-line camelcase
    filenames.user_image = user_image
    setUser({ ...user, user_image })
  }

  const onSaveClick = async () => {
    if (!user.name) {
      // eslint-disable-next-line no-alert
      alert('Please set your name')
      return
    }
    setSaving(true)
    const updates: UserProps = {
      name: user.name,
      shortBio: user.shortBio,
      social: user.social,
    }

    if (filenames.user_image) {
      updates.user_image = filenames.user_image
    }
    if (filenames.cover_image) {
      updates.cover_image = filenames.cover_image
    }

    const results = await updateMe(updates)
    if (results.error) {
      // TODO: show error message
    }
    onUpdate(results)
    updateUser(results)
    // TODO: show success message
    setEditing(false)
    setSaving(false)
    filenames.user_image = ''
    filenames.cover_image = ''
  }

  // const onUnfollowClick = () => {
  //   console.log('Unfollow')
  // }

  // const onFollowClick = () => {
  //   console.log('Follow')
  // }

  const onFeature = async () => {
    const result = await addFeature('User', user.id)
    // eslint-disable-next-line no-console
    console.log(result)
  }

  const social = user.social || {}

  return (
    <>
      {isEditing ? (
        <Curve className={styles.coverContainer} notransition>
          <ImageUploader onChange={onCoverChange}>
            {(
              { getRootProps, getInputProps, isDragActive },
              tmpImage,
              isUploading,
              progress,
            ) => (
              <div className={styles.uploadContainer}>
                <div
                  className={classnames(editStyles.btn, {
                    [editStyles.active]: isDragActive,
                  })}
                  {...getRootProps()}
                >
                  <div className={`${editStyles.content} tx-center`}>
                    {isUploading ? (
                      <Spinner size={64} className="mb-2" />
                    ) : (
                      <i
                        className={`solicon-camera ${editStyles.iconPhoto} mb-2`}
                      />
                    )}
                    <p className="tx-16 tx-center tx-w500">
                      {isUploading ? (
                        <FormattedMessage
                          id="user.edit.uploading"
                          defaultMessage="Uplaoding {progress}%"
                          values={{ progress: Math.floor(progress) }}
                        />
                      ) : (
                        <FormattedMessage
                          id="user.edit.cover"
                          defaultMessage="Change cover photo"
                        />
                      )}
                    </p>
                  </div>
                </div>
                <Parallax
                  src={tmpImage || user.cover_image}
                  alt=""
                  className={classnames(styles.coverImg, {
                    [styles.isUploading]: isUploading,
                  })}
                />
                <input {...getInputProps()} />
              </div>
            )}
          </ImageUploader>
        </Curve>
      ) : (
        <>
          {user.cover_image && (
            <Curve className={styles.coverContainer}>
              <Parallax src={user.cover_image} alt="" className={styles.coverImg} />
            </Curve>
          )}
        </>
      )}

      <div
        className={classnames('container-md', {
          [styles.containerPad]: !(user.cover_image || isEditing),
        })}
      >
        <div className="row">
          <div className="col-md-4 mb-3">
            <div className={classnames('circle mb-5', styles.userImage)}>
              {isEditing ? (
                <ImageUploader onChange={onAvatarChange}>
                  {(
                    { getRootProps, getInputProps, isDragActive },
                    tmpImage,
                    isUploading,
                    progress,
                  ) => (
                    <div className={styles.uploadContainer}>
                      <div
                        className={classnames(editStyles.btn, 'circle', {
                          [editStyles.active]: isDragActive,
                        })}
                        {...getRootProps()}
                      >
                        <div className={`${editStyles.content} tx-center`}>
                          {isUploading ? (
                            <Spinner size={64} className="mb-2" />
                          ) : (
                            <i
                              className={`solicon-camera ${editStyles.iconPhoto} mb-2`}
                            />
                          )}
                          <p className="tx-14 tx-center tx-w500 mb-0 px-4">
                            {isUploading ? (
                              <FormattedMessage
                                id="user.edit.uploading"
                                defaultMessage="Uplaoding {progress}%"
                                values={{ progress: Math.floor(progress) }}
                              />
                            ) : (
                              <FormattedMessage
                                id="user.edit.avatar"
                                defaultMessage="Change profile photo"
                              />
                            )}
                          </p>
                        </div>
                      </div>
                      <Avatar
                        src={tmpImage || user.user_image}
                        width="200"
                        alt={user.name}
                      />
                      <input {...getInputProps()} />
                    </div>
                  )}
                </ImageUploader>
              ) : (
                <Avatar src={user.user_image} width="200" alt={user.name} />
              )}
            </div>
            {isEditing ? (
              <label htmlFor="name" className={`${editStyles.wm400p} mb-3`}>
                <span className="tx-12">
                  <FormattedMessage
                    id="user.edit.name"
                    defaultMessage="Display Name"
                  />
                </span>
                <input
                  id="name"
                  type="text"
                  className={`${editStyles.input} ${editStyles.wm400p} tx-24 mb-3 p-2`}
                  value={user.name}
                  onChange={(e) => setUser({ ...user, name: e.target.value })}
                />
              </label>
            ) : (
              <h1 className="tx-center">{user.name}</h1>
            )}
            {!isEditing && (
              <h4 className="tx-18 tx-gish tx-w400 tx-center mb-4">
                @{user.username}
              </h4>
            )}
            {!isEditing && user.shortBio && (
              <Linkify className={`${styles.shortBio} tx-center tx-14 mb-2`}>
                {user.shortBio}
              </Linkify>
            )}
            {isEditing && (
              <label htmlFor="shortbio" className={`${editStyles.wm400p} mb-3`}>
                <span className="tx-12">
                  <FormattedMessage
                    id="user.edit.shortbio"
                    defaultMessage="Short Bio"
                  />
                </span>
                <textarea
                  id="shortbio"
                  className={`${styles.shortBio} ${editStyles.shortBio} tx-14 p-2`}
                  value={user.shortBio}
                  placeholder="please tell us about you."
                  onChange={(e) =>
                    setUser({ ...user, shortBio: e.target.value })
                  }
                />
              </label>
            )}
            {!isEditing && (
              <p className="tx-center tx-g700 tx-12 mb-4">
                <FormattedMessage
                  id="user.registeredat"
                  defaultMessage="Since {time}"
                  values={{
                    time: intl.formatDate(user.registered_at, {
                      year: 'numeric',
                      month: 'short',
                      day: '2-digit',
                    }),
                  }}
                />
              </p>
            )}
            {!isEditing && user.social && (
              <div className="d-flex justify-content-center">
                <SocialButton type="twitter" value={user.social.twitter} />
                <SocialButton type="facebook" value={user.social.facebook} />
                <SocialButton type="instagram" value={user.social.instagram} />
                <SocialButton type="youtube" value={user.social.youtube} />
                <SocialButton type="website" value={user.social.website} />
              </div>
            )}
            {!isEditing && isAdmin && (
              <div className="tx-center mt-3">
                <Button className={`${styles.btnEdit} lg`} onClick={onFeature}>
                  feature
                </Button>
              </div>
            )}
            {isEditing && (
              <div className={`mb-5 ${editStyles.wm400p}`}>
                <span className="tx-12">
                  <FormattedMessage
                    id="user.edit.social"
                    defaultMessage="Social Accounts"
                  />
                </span>
                <div className="d-flex align-items-center mt-2">
                  <div className="mr-2">
                    <i className="solicon-twitter-alt" />
                  </div>
                  <input
                    id="twitter"
                    type="text"
                    className={editStyles.input}
                    value={social.twitter || ''}
                    placeholder="Twitter account"
                    onChange={(e) =>
                      setUser({
                        ...user,
                        social: { ...user.social, twitter: e.target.value },
                      })
                    }
                  />
                </div>
                <span
                  className={`${editStyles.socialLink} tx-12 tx-gray mb-3 pl-4`}
                >
                  https://twitter.com/
                  <span className="tx-black tx-14">{social.twitter}</span>
                </span>
                <div className="d-flex align-items-center mt-2">
                  <div className="mr-2">
                    <i className="solicon-facebook" />
                  </div>
                  <input
                    id="facebook"
                    type="text"
                    className={editStyles.input}
                    value={social.facebook || ''}
                    placeholder="Facebook account / page"
                    onChange={(e) =>
                      setUser({
                        ...user,
                        social: { ...user.social, facebook: e.target.value },
                      })
                    }
                  />
                </div>
                <span
                  className={`${editStyles.socialLink} tx-12 tx-gray mb-3 pl-4`}
                >
                  https://www.facebook.com/
                  <span className="tx-black tx-14">{social.facebook}</span>
                </span>
                <div className="d-flex align-items-center mt-2">
                  <div className="mr-2">
                    <i className="solicon-instagram" />
                  </div>
                  <input
                    type="text"
                    className={editStyles.input}
                    value={social.instagram || ''}
                    placeholder="Instagram account"
                    onChange={(e) =>
                      setUser({
                        ...user,
                        social: { ...user.social, instagram: e.target.value },
                      })
                    }
                  />
                </div>
                <span
                  className={`${editStyles.socialLink} tx-12 tx-gray mb-3 pl-4`}
                >
                  https://instagram.com/
                  <span className="tx-black tx-14">{social.instagram}</span>
                </span>
                <div className="d-flex align-items-center mt-2">
                  <div className="mr-2">
                    <i className="solicon-youtube" />
                  </div>
                  <input
                    id="youtube"
                    type="text"
                    className={editStyles.input}
                    value={social.youtube || ''}
                    placeholder="Youtube account / page"
                    onChange={(e) =>
                      setUser({
                        ...user,
                        social: { ...user.social, youtube: e.target.value },
                      })
                    }
                  />
                </div>
                <span
                  className={`${editStyles.socialLink} tx-12 tx-gray mb-3 pl-4`}
                >
                  https://youtube.com/channel/
                  <span className="tx-black tx-14">{social.youtube}</span>
                </span>
                <div className="d-flex align-items-center mt-2">
                  <div className="mr-2">
                    <i className="solicon-website" />
                  </div>
                  <input
                    type="text"
                    className={editStyles.input}
                    value={social.website || ''}
                    placeholder="Your website / blog"
                    onChange={(e) =>
                      setUser({
                        ...user,
                        social: { ...user.social, website: e.target.value },
                      })
                    }
                  />
                </div>
              </div>
            )}
          </div>
          <div className="col-md-8">
            <div
              className={`d-md-flex align-items-center mb-5 ${styles.profileHeader}`}
            >
              <div className="ps-relative flex1">
                {isEditing && <div className={styles.mask} />}
                {/* ↓現状機能していない */}
                {/* <ul
                  className={`d-flex justify-content-center justify-content-md-start ${styles.profileNav}`}
                >
                  {user.sounds > 0 && (
                    <li>
                      <span className={styles.profileNavItem}>
                        <span className="d-block tx-oswald tx-w300 tx-14">
                          Sounds
                        </span>
                        <span className="d-block tx-oswald tx-w700 tx-24">
                          {user.sounds}
                        </span>
                      </span>
                    </li>
                  )}
                  {user.following > 0 && (
                    <li>
                      <span className={styles.profileNavItem}>
                        <span className="d-block tx-oswald tx-w300 tx-14">
                          Following
                        </span>
                        <span className="d-block tx-oswald tx-w700 tx-24">
                          {user.following}
                        </span>
                      </span>
                    </li>
                  )}
                  {user.follower > 0 && (
                    <li>
                      <span className={styles.profileNavItem}>
                        <span className="d-block tx-oswald tx-w300 tx-14">
                          Follower
                        </span>
                        <span className="d-block tx-oswald tx-w700 tx-24">
                          {user.follower}
                        </span>
                      </span>
                    </li>
                  )}
                </ul> */}
              </div>
              <div
                className={classnames('flex0 m-md-0 tx-center mt-5', {
                  [editStyles.buttons]: isEditing,
                })}
              >
                {isMe ? (
                  <>
                    {isEditing ? (
                      <>
                        {!isSaving && (
                          <Button className="link" onClick={onToggleEditClick}>
                            <FormattedMessage
                              id="app.cancel"
                              defaultMessage="Cancel"
                            />
                          </Button>
                        )}
                        <Button
                          className={`primary ${editStyles.btnSave}`}
                          onClick={onSaveClick}
                          disabled={isSaving}
                        >
                          {isSaving ? (
                            <>
                              <Spinner size={16} className="mr-2" />
                              <FormattedMessage
                                id="user.saveing"
                                defaultMessage="Saveing..."
                              />
                            </>
                          ) : (
                            <FormattedMessage
                              id="user.save"
                              defaultMessage="Save Profile"
                            />
                          )}
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          className={`${styles.btnEdit} lg`}
                          onClick={onToggleEditClick}
                        >
                          <FormattedMessage
                            id="user.edit"
                            defaultMessage="Edit Profile"
                          />
                        </Button>
                      </>
                    )}
                  </>
                ) : (
                  <>
                    {
                      // isFollowing ? (
                      //   <Button className="lg primary" onClick={onUnfollowClick}>
                      //     <FormattedMessage id="app.following" defaultMessage="Following" />
                      //   </Button>
                      // ) : (
                      //   <Button className="lg" onClick={onFollowClick}>
                      //     <FormattedMessage id="app.follow" defaultMessage="Follow" />
                      //   </Button>
                      // )
                    }
                  </>
                )}
              </div>
            </div>{' '}
            {/* end profileHeader */}
            <section className="ps-relative">
              {isEditing && <div className={styles.mask} />}
              {/* <h2 className="heading">
                <span className="text tx-oswald">Featured</span>
                </h2>
                <div className="mb-5">
                <Sound sound={soundData} />
              </div> */}

              <div className="mb-5">
                <Latest username={user.username} name={user.name} />
              </div>
            </section>
          </div>
        </div>
      </div>
    </>
  )
}

export default User
